import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { TABLE_ROWS_COUNT } from "../../shared/constants";
import { RootState } from "../../shared/types/common";
import {
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ApartmentOutlined,
} from "@ant-design/icons";
import { Space, Button, Col, Row, Modal, Drawer } from "antd";
import { BiFilterAlt, BiTransfer } from "react-icons/bi";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import GenericFormComponent from "../../shared/components/generic-form/generic-form.component";
import { useForm } from "antd/lib/form/Form";
import CreateEditDepartmentComponent from "./components/create-edit-department.component";
import {
  deleteDepartment,
  getDepartmentList,
} from "../../core/store/reducers/department.slice";
import { accessAllowed, showNotification } from "../../shared/utils/utils";
import { Client } from "../../core/api-client";
import { API_GET_ALL_STAFF_LIST } from "../../shared/routes/private.routes";
import ActivityLogComponent from "../../shared/components/activity-log/activity-log.component";
import { allPermissions } from "../../shared/constants/constants";
import { ActiveFilterChipsBar } from "../../shared/components/active-filter-chips/active-filter-chips.component";
import { isFilterValueActive } from "../../shared/utils/active-filters";

const DEPT_FILTER_LABELS: Record<string, string> = {
  departmentName: "Name",
  status: "Status",
  headOfDepartmentId: "Head of department",
};

function deptFilterValueText(
  key: string,
  value: any,
  staffList: any[]
): string {
  switch (key) {
    case "departmentName":
      return String(value).trim();
    case "status":
      return Array.isArray(value) ? value.join(", ") : String(value);
    case "headOfDepartmentId": {
      const ids = Array.isArray(value) ? value : [value];
      return ids
        .map(
          (id: string) =>
            staffList.find((s) => s.staffId === id)?.staffName ?? id
        )
        .join(", ");
    }
    default:
      return Array.isArray(value) ? value.join(", ") : String(value);
  }
}

const DEPT_FILTER_CLEAR: Record<string, any> = {
  departmentName: undefined,
  status: [],
  headOfDepartmentId: [],
};

const ManageDepartmentPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = useForm();
  const { loadingDepartment, departmentList, pagination } = useSelector(
    (state: RootState) => state.department
  );
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [departmentIdToDelete, setDepartmentIdToDelete] = useState("");
  const { screenType } = useSelector((state: RootState) => state.settings);
  const { user } = useSelector((state: RootState) => state.auth);
  // List local state
  const [departmentListData, setDepartmentListData] = useState([] as any);

  // Flags
  const [createEditOpen, setCreateEditOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null as any);
  const [filterVisible, setFilterVisible] = useState(false);

  // Actions and selections
  const [sorter, setSorter] = useState({ sort_by: "", sort_type: "" });
  const [filters, setFilters] = useState({} as any);
  const [staffList, setStaffList] = useState<any[]>([]);
  const [openLogs, setOpenLogs] = useState(false);

  useEffect(() => {
    loadList();
  }, [filters, sorter]);
  const loadList = (page: any = null) => {
    const data = {
      page: page ? page : pagination?.page ? pagination?.page : 1,
      limit: pagination?.limit || TABLE_ROWS_COUNT,
      ...sorter,
    };
    let filter = {} as any;
    Object.keys(filters)?.map((val: any) => {
      if (filters[val]) filter[val] = filters[val];
    });
    dispatch(getDepartmentList({ params: data, body: filter }));
  };
  const createEntityListner = () => {
    setCreateEditOpen(true);
    setSelectedEntity(null);
  };
  const editEntityListner = (entity: any) => {
    setCreateEditOpen(true);
    setSelectedEntity(entity);
  };
  useEffect(() => {
    setDepartmentListData(
      departmentList.map((obj: any, index: number) => {
        return { key: index, ...obj };
      })
    );
  }, [departmentList]);
  const onFilterApply = (values: any) => {
    setFilters(values);
  };

  const activeFilterEntries = useMemo(() => {
    return Object.keys(filters)
      .filter((k) => isFilterValueActive(filters[k]))
      .map((key) => ({
        key,
        fieldLabel: DEPT_FILTER_LABELS[key] || key,
        valueText: deptFilterValueText(key, filters[key], staffList),
      }));
  }, [filters, staffList]);

  const clearAllFilters = useCallback(() => {
    setFilters({});
    form.resetFields();
  }, [form]);

  const removeFilter = useCallback(
    (key: string) => {
      setFilters((prev: any) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      const cleared =
        key in DEPT_FILTER_CLEAR ? DEPT_FILTER_CLEAR[key] : undefined;
      form.setFieldsValue({ [key]: cleared });
    },
    [form]
  );

  const onPageChange = (pageNumber: number) => {
    loadList(pageNumber);
  };
  const hasAnyDepartmentAccess =
    accessAllowed(myPermissions, allPermissions.UPDATE_DEPARTMENT) ||
    accessAllowed(myPermissions, allPermissions.DELETE_DEPARTMENT) ||
    accessAllowed(myPermissions, allPermissions.DEPARTMENT_ACTIVITY_LOGS);
  const columns = [
    {
      title: "Department Name",
      dataIndex: "departmentName",
      sorter: false,
      key: "departmentName",
    },
    {
      title: "Head of Department",
      dataIndex: "headOfDepartment",
      sorter: false,
      key: "headOfDepartment",
    },
    {
      title: "Status",
      dataIndex: "status",
      sorter: false,
      key: "status",
    },
    ...(hasAnyDepartmentAccess
      ? [
          {
            title: "Actions",
            key: "actions",
            fixed: "right",
            render: (text: any, record: any) => (
              <Space>
                {accessAllowed(
                  myPermissions,
                  allPermissions.UPDATE_DEPARTMENT
                ) && (
                  <Button
                    type="link"
                    onClick={() => editEntityListner(record)}
                    icon={<EditOutlined />}
                  />
                )}
                {accessAllowed(
                  myPermissions,
                  allPermissions.DELETE_DEPARTMENT
                ) && (
                  <Button
                    type="link"
                    onClick={() => handleDeleteModal(record?.departmentId)}
                    icon={<DeleteOutlined style={{color:"#FF0000"}}  />}
                  />
                )}
                {accessAllowed(
                  myPermissions,
                  allPermissions.DEPARTMENT_ACTIVITY_LOGS
                ) && (
                  <Button
                    type="link"
                    onClick={() => handleOpenLogs(record)}
                    icon={<EyeOutlined />}
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ];
  const handleDelete = (departmentId: string) => {
    dispatch(deleteDepartment(departmentId)).then((val: any) => {
      if (val?.payload?.data) {
        showNotification("Department Deleted Successfully", "success");
        loadList();
        setDeleteModalVisible(false); // Close the modal after successful deletion
      }
    });
  };

  const handleOpenLogs = (record: any) => {
    setOpenLogs(true);
    setDepartmentIdToDelete(record?.departmentId);
    setSelectedEntity(record);
  };

  const onCloseLogs = () => {
    setOpenLogs(false);
    setDepartmentIdToDelete("");
  };

  const handleDeleteModal = (departmentId: string) => {
    setDeleteModalVisible(true);
    setDepartmentIdToDelete(departmentId);
  };

  const onDeleteConfirm = () => {
    handleDelete(departmentIdToDelete);
  };

  const onDeleteCancel = () => {
    setDeleteModalVisible(false);
    setDepartmentIdToDelete("");
  };

  // const handleDelete = (departmentId: string)=>{
  //     dispatch(deleteDepartment(departmentId)).then(
  //         (val: any) => {
  //           if (val?.payload?.data) {
  //             showNotification("Department Deleted", "success");
  //             loadList();
  //           }
  //         }
  //       );
  // }
  const onChangeHandler = (_pagination: any, _filters: any, _sorter: any) => {
    if (_sorter.order === undefined) {
      setSorter({ sort_by: "", sort_type: "" });
    } else {
      let order = "";
      let columnKey = "";
      if (_sorter.order === "ascend") order = "asc";
      else order = "desc";

      if (_sorter.columnKey === "enabled") {
        columnKey = "status";
      } else if (_sorter.columnKey === "displayName") {
        columnKey = "name";
      } else {
        columnKey = _sorter.columnKey;
      }
      setSorter({ sort_by: columnKey, sort_type: order });
    }
  };
  const createEditSubmit = () => {
    setCreateEditOpen(false);
    loadList();
  };
  useEffect(() => {
    // dispatch(getList()).then((val: any) => {
    //   if (val?.payload?.data?.data?.length > 0) {
    //     setList(
    //       val.payload.data.data.map((val: any) => {
    //         return { label: val.name, value: val._id };
    //       })
    //     );
    //   }
    // });
    loadList();
  }, []);
  const onValuesChange = (value: any, _allValues: any) => {};

  useEffect(() => {
    fetchStaffList();
  }, []);

  const fetchStaffList = async () => {
    const response = await Client.get(API_GET_ALL_STAFF_LIST);
    if (response?.data?.data) {
      setStaffList(response?.data?.data);
    } else {
      setStaffList([]);
    }
  };

  const staffDropdownOptions = staffList.map((staff) => ({
    value: staff?.staffId,
    label: staff?.staffName,
  }));

  const formProps = {
    form: form,
    name: "manage-department-filters",
    enableEnterSubmit: true,
    formElements: [
      {
        name: "departmentName",
        label: "",
        placeholder: "Department Name Search",
        type: "Input",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
      },
      {
        name: "status",
        label: "",
        placeholder: "Status",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        mode: "multiple",
        options: [
          { value: "ACTIVE", label: "ACTIVE" },
          { value: "INACTIVE", label: "INACTIVE" },
        ],
      },
      {
        name: "headOfDepartmentId",
        label: "",
        placeholder: "Dead Of Department",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        mode: "multiple",
        options: staffDropdownOptions,
      },
    ],
    initialValues: {
      name: "",
    },
    extraButtons: [
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm",
      },
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Apply",
        submitButtonType: "primary",
      },
    ],
    onFinish: onFilterApply,
    onFinishFailed: null,
    onValuesChange: onValuesChange,
  };
  const headerButtons = (
    <Space size="middle" className="app-page-actions">
      {accessAllowed(myPermissions, allPermissions.CREATE_DEPARTMENT) && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={createEntityListner}
        >
          <span className="font-size-14">
            New {!screenType?.isPortrait && "Department"}
          </span>
        </Button>
      )}
      <Button
        shape="circle"
        type="primary"
        icon={<BiFilterAlt />}
        onClick={() => setFilterVisible(!filterVisible)}
        title={filterVisible ? "Hide filters" : "Show filters"}
      />
    </Space>
  );

  return (
    <div className="app-page">
      <div className="app-page-header">
        <Row align="middle" justify="space-between" wrap>
          <Col flex="1" style={{ minWidth: 0 }}>
            <div className="app-page-title-wrap">
              <div className="app-page-icon">
                <ApartmentOutlined />
              </div>
              <h1 className="app-page-title">Manage Department</h1>
            </div>
            <p className="app-page-subtitle">
              View and manage departments
            </p>
          </Col>
          <Col>{headerButtons}</Col>
        </Row>
      </div>
      {filterVisible && (
        <div className="app-filter-bar">
          <div className="app-filter-title">
            <BiFilterAlt style={{ fontSize: 14 }} />
            Filters
          </div>
          <div className="table-filter-form">
            <GenericFormComponent {...formProps} />
          </div>
        </div>
      )}

      <ActiveFilterChipsBar
        show={!filterVisible}
        entries={activeFilterEntries}
        onRemove={removeFilter}
        onClearAll={clearAllFilters}
      />

      <div className="app-table-card">
        <DataTableComponent
        pagination={true}
        loading={loadingDepartment}
        dataSource={departmentListData}
        columns={columns}
        onChange={onChangeHandler}
        defaultPage={0}
        currentPage={pagination.page}
        totalRows={pagination?.total}
        onPageChange={onPageChange}
      />
      </div>
      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={onDeleteConfirm}
        onCancel={onDeleteCancel}
      >
        <p>Are you sure you want to delete this department?</p>
      </Modal>
      {createEditOpen && (
        <CreateEditDepartmentComponent
          onClose={() => setCreateEditOpen(false)}
          visible={createEditOpen}
          selectedEntity={selectedEntity}
          onSubmit={createEditSubmit}
        />
      )}
      {openLogs && (
        <Drawer open={openLogs} onClose={onCloseLogs} title="Log Details">
          <ActivityLogComponent
            id={selectedEntity?.departmentId || departmentIdToDelete}
            record={selectedEntity}
            module="Department"
          />
        </Drawer>
      )}
    </div>
  );
};
export default ManageDepartmentPage;
